import { Button, EmptyState, Select, Spinner } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PickRound, Shipment } from '@warehouse/shared';
import { Boxes, PackagePlus } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { formatDate } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { pickingKeys, roundsApi, shipmentsApi } from '../api';
import { ShipmentStatusBadge } from '../components/StatusBadge';

/**
 * The packing bench.
 *
 * Left: rounds that are picked and waiting to be packed — one card per order,
 * with the action that creates its shipment. Right: shipments already in
 * progress. This is the screen a packer keeps open all afternoon, so it is a
 * work list rather than a table: the next thing to do is always a button.
 */
export function PackingPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const roundsState = useMemo(
    () => ({ filters: { status: ['picked', 'packed'] }, sort: 'deliveryDate', pageSize: 100 }),
    [],
  );
  const shipmentsState = useMemo(
    () => ({ filters: { status: ['draft', 'booked'] }, sort: '-createdAt', pageSize: 100 }),
    [],
  );

  const rounds = useQuery({
    queryKey: pickingKeys.rounds(roundsState),
    queryFn: () => roundsApi.list(roundsState),
  });
  const shipments = useQuery({
    queryKey: pickingKeys.shipments(shipmentsState),
    queryFn: () => shipmentsApi.list(shipmentsState),
  });
  useLiveQuery(['pick-rounds', 'shipments', 'sales-orders'], ['picking'], { exact: false });
  useLiveQuery(['shipments'], ['shipments'], { exact: false });

  const create = useMutation({
    mutationFn: (orderId: string) => shipmentsApi.create({ orderId, type: 'carrier' }),
    onSuccess: async (shipment) => {
      toastSuccess(t('packing.shipmentCreated'));
      await queryClient.invalidateQueries({ queryKey: ['shipments'] });
      void navigate(`/orders/shipments/${shipment.id}`);
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const shipmentByOrder = useMemo(() => {
    const map = new Map<string, Shipment>();
    for (const shipment of shipments.data?.items ?? []) map.set(shipment.orderId, shipment);
    return map;
  }, [shipments.data]);

  const pending = (rounds.data?.items ?? []).flatMap((round: PickRound) =>
    round.orders.map((order) => ({ round, order })),
  );

  return (
    <>
      <PageHeader title={t('packing.title')} subtitle={t('packing.subtitle')} />

      {rounds.isLoading || shipments.isLoading ? <Spinner /> : null}

      {!rounds.isLoading && pending.length === 0 ? (
        <EmptyState icon={<Boxes />} title={t('packing.readyToPack')} description={t('rounds.emptyDescription')} />
      ) : null}

      <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="packing-worklist">
        {pending.map(({ round, order }) => {
          const shipment = shipmentByOrder.get(order.orderId);
          return (
            <li
              key={`${round.id}-${order.orderId}`}
              className="flex flex-col gap-3 rounded-xl border border-surface-border bg-surface p-4"
            >
              <div>
                <span className="font-mono text-sm font-semibold">{order.orderNo}</span>
                <span className="block text-sm text-ink-muted">{order.restaurantName}</span>
                <span className="block text-xs text-ink-muted">
                  {formatDate(order.deliveryDate)} · {round.roundNo}
                </span>
              </div>

              {shipment ? (
                <div className="flex items-center gap-2">
                  <ShipmentStatusBadge status={shipment.status} />
                  <span className="font-mono text-xs">{shipment.shipmentNo}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="ml-auto"
                    onClick={() => {
                      void navigate(`/orders/shipments/${shipment.id}`);
                    }}
                  >
                    {t('packing.pallets')}
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  icon={<PackagePlus className="size-4" />}
                  loading={create.isPending && create.variables === order.orderId}
                  onClick={() => {
                    create.mutate(order.orderId);
                  }}
                >
                  {t('packing.createShipment')}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Kept mounted so the shipment filter chip stays usable on a tablet. */}
      <div className="mt-8 max-w-xs">
        <Select
          label={t('shipments.title')}
          value=""
          placeholder={t('shipments.title')}
          options={(shipments.data?.items ?? []).map((shipment) => ({
            value: shipment.id,
            label: `${shipment.shipmentNo} — ${shipment.restaurantName}`,
          }))}
          onChange={(event) => {
            if (event.target.value) void navigate(`/orders/shipments/${event.target.value}`);
          }}
        />
      </div>
    </>
  );
}
